/**
 * "Ricontatta" sulle chat: regole pure (niente React ne' Supabase, i test girano in node).
 *
 * Dove vive il promemoria:
 * - chat collegata a un Lead/Cliente: un Task commerciale (sales_activities, tipo 'task')
 *   con metadata.source = 'chat_recontact' e metadata.chat_jid, cosi' compare anche nelle
 *   attivita' del CRM;
 * - chat non collegata: colonne recontact_* di whatsapp_chat_meta (migration 20260928_chat_ricontatta_promemoria).
 */
import { lastActivityMs, type ChatLike } from './chatListRules';

export const RECONTACT_SOURCE = 'chat_recontact';
/** Ora del giorno proposta per ricontattare (ora locale). */
export const RECONTACT_HOUR = 9;

export type RecontactChoice = 'tomorrow' | '3d' | '1w';

export const RECONTACT_CHOICES: Array<{ value: RecontactChoice; label: string }> = [
  { value: 'tomorrow', label: 'Domani' },
  { value: '3d', label: 'Tra 3 giorni' },
  { value: '1w', label: 'Tra una settimana' },
];

export interface ChatRecontact {
  chatKey: string;
  dueAt: string;
  note: string | null;
  /** Da quando vale il promemoria: un messaggio del cliente successivo = "ha risposto". */
  setAt: string;
  /** Task commerciale collegato (chat con Lead/Cliente); null = riga di whatsapp_chat_meta. */
  taskId: string | null;
  clientId: string | null;
}

export interface RecontactStatus {
  due: boolean;
  replied: boolean;
}

/** Data di scadenza per una scelta rapida: il giorno indicato alle 9:00. */
export function recontactDueFor(choice: RecontactChoice, now: Date = new Date()): Date {
  const giorni = choice === 'tomorrow' ? 1 : choice === '3d' ? 3 : 7;
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + giorni, RECONTACT_HOUR, 0, 0, 0);
}

/** Data a scelta dal campo data (AAAA-MM-GG) alle 9:00; null se non valida. */
export function recontactDueFromDateInput(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), RECONTACT_HOUR, 0, 0, 0);
  return Number.isNaN(d.getTime()) || d.getDate() !== Number(m[3]) ? null : d;
}

/** Valore minimo del campo data: domani (AAAA-MM-GG, ora locale). */
export function minDateInput(now: Date = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * Scaduto = da ricontattare adesso. Ha risposto = l'ultimo messaggio e' del cliente ed e'
 * arrivato dopo che il promemoria e' stato impostato (o rimandato).
 */
export function recontactStatus(r: ChatRecontact | null | undefined, chat: ChatLike | null | undefined, now: number = Date.now()): RecontactStatus {
  if (!r) return { due: false, replied: false };
  const due = new Date(r.dueAt).getTime() <= now;
  const setAt = new Date(r.setAt).getTime();
  const last = chat ? lastActivityMs(chat) : 0;
  const replied = !!chat?.lastMessage && !chat.lastMessage.fromMe && Number.isFinite(setAt) && last > setAt;
  return { due, replied };
}

/** Data breve per etichette e suggerimenti: "3 ott". */
export function formatRecontactDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
}

/** Testo della striscia sopra il campo di scrittura. */
export function recontactStripText(r: ChatRecontact, status: RecontactStatus): string {
  const nota = r.note?.trim() ? ` (${r.note.trim()})` : '';
  if (status.replied && !status.due) return `Ha risposto prima del ricontatto previsto il ${formatRecontactDate(r.dueAt)}${nota}`;
  return `Promemoria: ricontattare${nota}`;
}

/**
 * Chat da ricontattare in cima alla lista (prima anche delle fissate). Una chat archiviata
 * scaduta torna fra le visibili. L'ordine di partenza resta invariato dentro ogni gruppo.
 */
export function withDueRecontactsOnTop<T extends ChatLike>(
  visible: T[],
  archived: T[],
  isDue: (chat: T) => boolean,
): { visible: T[]; archived: T[] } {
  const due: T[] = [];
  const rest: T[] = [];
  for (const c of visible) (isDue(c) ? due : rest).push(c);
  const stillArchived: T[] = [];
  for (const c of archived) (isDue(c) ? due : stillArchived).push(c);
  return { visible: [...due, ...rest], archived: stillArchived };
}

/** Riga di sales_activities letta dal DB (solo i campi che servono qui). */
export interface RecontactTaskRow {
  id: string;
  due_date: string;
  description: string | null;
  created_at: string | null;
  client_id: string | null;
  lead_id: string | null;
  metadata: { source?: string; chat_jid?: string; note?: string | null; set_at?: string } | null;
}

export function recontactFromTask(row: RecontactTaskRow): ChatRecontact | null {
  const chatKey = row.metadata?.chat_jid;
  if (row.metadata?.source !== RECONTACT_SOURCE || !chatKey) return null;
  return {
    chatKey,
    dueAt: row.due_date,
    note: row.metadata.note ?? null,
    setAt: row.metadata.set_at || row.created_at || row.due_date,
    taskId: row.id,
    clientId: row.client_id || row.lead_id || null,
  };
}

export interface RecontactMetaRow {
  chat_jid: string;
  recontact_at: string | null;
  recontact_note: string | null;
  recontact_set_at: string | null;
}

export function recontactFromMeta(row: RecontactMetaRow): ChatRecontact | null {
  if (!row.recontact_at) return null;
  return {
    chatKey: row.chat_jid,
    dueAt: row.recontact_at,
    note: row.recontact_note,
    setAt: row.recontact_set_at || row.recontact_at,
    taskId: null,
    clientId: null,
  };
}

/** Titolo del Task commerciale: "Ricontattare Mario Rossi su WhatsApp". */
export function recontactTaskTitle(chatName: string): string {
  const nome = chatName.trim() || 'il contatto';
  return `Ricontattare ${nome} su WhatsApp`;
}

export function recontactTaskMetadata(chatKey: string, note: string | null, setAt: string) {
  return { source: RECONTACT_SOURCE, channel: 'whatsapp', chat_jid: chatKey, note: note?.trim() || null, set_at: setAt };
}

/** Piu' promemoria aperti sulla stessa chat: vale quello che scade prima. */
export function indexRecontacts(list: Array<ChatRecontact | null>): Map<string, ChatRecontact> {
  const map = new Map<string, ChatRecontact>();
  for (const r of list) {
    if (!r) continue;
    const prev = map.get(r.chatKey);
    if (!prev || new Date(r.dueAt).getTime() < new Date(prev.dueAt).getTime()) map.set(r.chatKey, r);
  }
  return map;
}
